import { ReactNode, useState, useEffect, useRef } from "react";
import C from "classnames";

import { useUserBanner } from "../../hooks/useUserBanner";

import { UserBanner } from "../UserBanner/UserBanner";
import { UserNavBar } from "../UserNavBar/UserNavBar";
import S from "./Header.module.scss";

export interface HeaderProps {
  breadcrumbs?: any;
  dialogTemplate?: any;
  fullWidth?: boolean;
  showBanner?: boolean;
  showNavBar?: boolean;
  children?: ReactNode;
  className?: string;
}

/**
 * Universal Header component for SJ UI Toolkit
 * Features:
 * - Fixed positioning
 * - Scroll background effect
 * - Accepts breadcrumbs, dialogTemplate, fullWidth, and custom children
 * - Used in KA, Learn, Champions, User profile layouts
 */
export function Header({
  breadcrumbs,
  dialogTemplate,
  fullWidth = false,
  showBanner = true,
  showNavBar = true,
  children,
  className,
}: HeaderProps) {
  const { banner, clearBanner } = useUserBanner();
  const [isScrolled, setIsScrolled] = useState(false);
  const navbarRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 0);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    // Set --navbar-height CSS variable to the actual height of the navbar
    const root = document.documentElement;
    const navbarHeight = navbarRef.current ? navbarRef.current.offsetHeight : 0;
    root.style.setProperty("--navbar-height", `${navbarHeight}px`);
  }, [showBanner, banner]);

  return (
    <header className={C(S.header, { [S.headerScrolled]: isScrolled }, className)}>
      {showBanner && banner && (
        <UserBanner banner={banner} handleClear={clearBanner} fullWidth={fullWidth} />
      )}
      {showNavBar && (
        <UserNavBar
          breadcrumbs={breadcrumbs}
          dialogTemplate={dialogTemplate}
          navbarRef={navbarRef}
        />
      )}
      {children}
    </header>
  );
}
